// src/components/common/vibe/CelebrationHub.js
import React, { useState, useEffect } from 'react';
import { Cake, Award, Calendar, User } from 'lucide-react';
import vibeService from '../../../services/vibe.service';
import CelebrationPostModal from './CelebrationPostModal';
import { useEmployees } from '../../../contexts/EmployeeContext';

const CelebrationHub = ({ theme, onPostCreated }) => {
    const { employees } = useEmployees();
    const [celebrations, setCelebrations] = useState({ birthdays: [], work_anniversaries: [] });
    const [upcomingBirthdays, setUpcomingBirthdays] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [celebrationType, setCelebrationType] = useState(null);
    const [showModal, setShowModal] = useState(false);

    useEffect(() => {
        fetchCelebrations();
    }, [employees]);

    const fetchCelebrations = async () => {
        setLoading(true);
        try {
            const response = await vibeService.getCelebrations();
            //console.log('🎉 Celebrations data:', response.data);
            setCelebrations(response.data);

            // If no birthdays today, fetch upcoming birthdays
            if (response.data.birthdays.length === 0) {
                await fetchUpcomingBirthdays();
            }
        } catch (error) {
            console.error('❌ Error fetching celebrations:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchUpcomingBirthdays = async () => {
        try {
            // Try backend first
            const response = await vibeService.getUpcomingBirthdays();
            if (response.data && response.data.length > 0) {
                setUpcomingBirthdays(response.data);
                return;
            }
        } catch (error) {
            console.log('Backend upcoming birthdays not available, calculating from employee data');
        }

        // Fallback: Calculate from employee context
        if (!employees || employees.length === 0) {
            setUpcomingBirthdays([]);
            return;
        }

        const today = new Date();
        today.setHours(0, 0, 0, 0);

        // Calculate upcoming birthdays from employee data
        const upcomingList = employees
            .filter(emp => emp.date_of_birth) // Only employees with DOB
            .map(emp => {
                const dob = new Date(emp.date_of_birth);
                const thisYear = today.getFullYear();

                // Get this year's birthday
                let nextBirthday = new Date(thisYear, dob.getMonth(), dob.getDate());
                nextBirthday.setHours(0, 0, 0, 0);

                // If birthday has passed this year, use next year
                if (nextBirthday < today) {
                    nextBirthday = new Date(thisYear + 1, dob.getMonth(), dob.getDate());
                }

                // Calculate days until birthday
                const daysUntil = Math.ceil((nextBirthday - today) / (1000 * 60 * 60 * 24));

                return {
                    ...emp,
                    nextBirthday,
                    daysUntil
                };
            })
            .filter(emp => emp.daysUntil > 0 && emp.daysUntil <= 30) // Next 30 days, exclude today
            .sort((a, b) => a.daysUntil - b.daysUntil); // Sort by nearest first

        setUpcomingBirthdays(upcomingList);
    };

    const getPronoun = (gender) => {
        if (!gender) return 'their';
        const genderLower = gender.toLowerCase();
        if (genderLower === 'male') return 'his';
        if (genderLower === 'female') return 'her';
        return 'their';
    };

    const getOrdinal = (years) => {
        if (years === 1) return '1st';
        if (years === 2) return '2nd';
        if (years === 3) return '3rd';
        return `${years}th`;
    };

    const getDaysUntilBirthday = (dateOfBirth) => {
        if (!dateOfBirth) return null;

        const today = new Date();
        const dob = new Date(dateOfBirth);

        // Get this year's birthday
        const thisYearBirthday = new Date(today.getFullYear(), dob.getMonth(), dob.getDate());

        // If birthday has passed this year, check next year
        if (thisYearBirthday < today) {
            thisYearBirthday.setFullYear(today.getFullYear() + 1);
        }

        // Calculate days difference
        const diffTime = thisYearBirthday - today;
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        return diffDays;
    };

    const formatDaysUntil = (days) => {
        if (days === 0) return 'Today';
        if (days === 1) return 'Tomorrow';
        if (days <= 7) return `In ${days} days`;
        if (days <= 30) return `In ${Math.floor(days / 7)} week${Math.floor(days / 7) > 1 ? 's' : ''}`;
        return `On ${new Date(new Date().setDate(new Date().getDate() + days)).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'Asia/Kolkata' })}`;
    };

    const handleCelebrationClick = (employee, type) => {
        setSelectedEmployee(employee);
        setCelebrationType(type);
        setShowModal(true);
    };

    const handleModalClose = () => {
        setShowModal(false);
        setSelectedEmployee(null);
        setCelebrationType(null);
    };

    if (loading) {
        return (
            <div className={`rounded-xl border p-6 ${
                theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
            }`}>
                <div className="flex items-center justify-center py-8">
                    <div className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                        Loading celebrations...
                    </div>
                </div>
            </div>
        );
    }

    const noCelebrations = celebrations.birthdays.length === 0 && celebrations.work_anniversaries.length === 0;

    return (
        <div className={`rounded-xl border ${
            theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
        }`}>
            {/* Header */}
            <div className={`p-4 border-b ${
                theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
            }`}>
                <h3 className={`text-lg font-semibold flex items-center gap-2 ${
                    theme === 'dark' ? 'text-white' : 'text-gray-900'
                }`}>
                    <Calendar className="w-5 h-5 text-purple-500" />
                    Let's Celebrate
                </h3>
            </div>

            {/* Content */}
            <div className="p-4 space-y-4">
                {noCelebrations ? (
                    upcomingBirthdays.length > 0 ? (
                        <div className="space-y-3">
                            <div className="flex items-center gap-2 mb-2">
                                <Cake className="w-4 h-4 text-blue-500" />
                                <h4 className={`font-semibold text-sm ${
                                    theme === 'dark' ? 'text-white' : 'text-gray-900'
                                }`}>
                                    Upcoming Birthdays
                                </h4>
                            </div>
                            {upcomingBirthdays.slice(0, 5).map((employee) => {
                                const daysUntil = getDaysUntilBirthday(employee.date_of_birth);
                                return (
                                    <div
                                        key={employee.id}
                                        className={`p-3 rounded-lg ${
                                            theme === 'dark' ? 'bg-gray-750' : 'bg-blue-50'
                                        }`}
                                    >
                                        <div className="flex items-start gap-3">
                                            {employee.profile_picture_url ? (
                                                <img
                                                    src={employee.profile_picture_url}
                                                    alt={`${employee.first_name} ${employee.last_name}`}
                                                    className="w-10 h-10 rounded-full object-cover"
                                                />
                                            ) : (
                                                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                                                    theme === 'dark' ? 'bg-gray-600' : 'bg-blue-200'
                                                }`}>
                                                    <User className="w-5 h-5 text-blue-600" />
                                                </div>
                                            )}
                                            <div className="flex-1 min-w-0">
                                                <p className={`font-medium text-sm ${
                                                    theme === 'dark' ? 'text-white' : 'text-gray-900'
                                                }`}>
                                                    {employee.first_name} {employee.last_name}
                                                </p>
                                                {employee.designation && (
                                                    <p className={`text-xs ${
                                                        theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                                                    }`}>
                                                        {employee.designation}
                                                    </p>
                                                )}
                                                <p className={`text-xs mt-1 ${
                                                    theme === 'dark' ? 'text-blue-400' : 'text-blue-600'
                                                } font-medium`}>
                                                    🎂 {formatDaysUntil(daysUntil)}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="text-center py-6">
                            <Calendar className={`w-12 h-12 mx-auto mb-3 ${
                                theme === 'dark' ? 'text-gray-600' : 'text-gray-400'
                            }`} />
                            <p className={`text-sm ${
                                theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                            }`}>
                                No celebrations today
                            </p>
                        </div>
                    )
                ) : (
                    <>
                        {/* Today's Birthdays */}
                        {celebrations.birthdays.length > 0 && (
                            <div className="space-y-3">
                                <div className="flex items-center gap-2 mb-2">
                                    <Cake className="w-4 h-4 text-pink-500" />
                                    <h4 className={`font-semibold text-sm ${
                                        theme === 'dark' ? 'text-white' : 'text-gray-900'
                                    }`}>
                                        Today's Birthdays
                                    </h4>
                                </div>
                                {celebrations.birthdays.map((employee) => (
                                    <div
                                        key={employee.id}
                                        onClick={() => handleCelebrationClick(employee, 'birthday')}
                                        className={`p-3 rounded-lg cursor-pointer ${
                                            theme === 'dark' ? 'bg-gray-750 hover:bg-gray-700' : 'bg-gray-50 hover:bg-gray-100'
                                        } transition-colors`}
                                    >
                                        <div className="flex items-start gap-3">
                                            {employee.profile_picture_url ? (
                                                <img
                                                    src={employee.profile_picture_url}
                                                    alt={`${employee.first_name} ${employee.last_name}`}
                                                    className="w-10 h-10 rounded-full object-cover"
                                                />
                                            ) : (
                                                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                                                    theme === 'dark' ? 'bg-gray-600' : 'bg-gray-300'
                                                }`}>
                                                    <User className="w-5 h-5 text-gray-500" />
                                                </div>
                                            )}
                                            <div className="flex-1 min-w-0">
                                                <p className={`font-medium text-sm ${
                                                    theme === 'dark' ? 'text-white' : 'text-gray-900'
                                                }`}>
                                                    {employee.first_name} {employee.last_name}
                                                </p>
                                                {employee.designation && (
                                                    <p className={`text-xs ${
                                                        theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                                                    }`}>
                                                        {employee.designation}
                                                    </p>
                                                )}
                                                <p className={`text-xs mt-1 italic ${
                                                    theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                                                }`}>
                                                    <span className="text-pink-500">🎂</span> {employee.first_name} is celebrating {getPronoun(employee.gender)} birthday today! Let's wish them a great day.
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Today's Work Anniversaries */}
                        {celebrations.work_anniversaries.length > 0 && (
                            <div className="space-y-3">
                                <div className="flex items-center gap-2 mb-2">
                                    <Award className="w-4 h-4 text-amber-500" />
                                    <h4 className={`font-semibold text-sm ${
                                        theme === 'dark' ? 'text-white' : 'text-gray-900'
                                    }`}>
                                        Today's Work Anniversaries
                                    </h4>
                                </div>
                                {celebrations.work_anniversaries.map((employee) => (
                                    <div
                                        key={employee.id}
                                        onClick={() => handleCelebrationClick(employee, 'work_anniversary')}
                                        className={`p-3 rounded-lg cursor-pointer ${
                                            theme === 'dark' ? 'bg-gray-750 hover:bg-gray-700' : 'bg-gray-50 hover:bg-gray-100'
                                        } transition-colors`}
                                    >
                                        <div className="flex items-start gap-3">
                                            {employee.profile_picture_url ? (
                                                <img
                                                    src={employee.profile_picture_url}
                                                    alt={`${employee.first_name} ${employee.last_name}`}
                                                    className="w-10 h-10 rounded-full object-cover"
                                                />
                                            ) : (
                                                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                                                    theme === 'dark' ? 'bg-gray-600' : 'bg-gray-300'
                                                }`}>
                                                    <User className="w-5 h-5 text-gray-500" />
                                                </div>
                                            )}
                                            <div className="flex-1 min-w-0">
                                                <p className={`font-medium text-sm ${
                                                    theme === 'dark' ? 'text-white' : 'text-gray-900'
                                                }`}>
                                                    {employee.first_name} {employee.last_name}
                                                </p>
                                                {employee.designation && (
                                                    <p className={`text-xs ${
                                                        theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                                                    }`}>
                                                        {employee.designation}
                                                    </p>
                                                )}
                                                <p className={`text-xs mt-1 italic ${
                                                    theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                                                }`}>
                                                    <span className="text-amber-500">🏆</span> {employee.first_name} is celebrating {getPronoun(employee.gender)} {getOrdinal(employee.years_of_service)} work anniversary! Let's thank them for their hard work and support in company's growth.
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* Celebration Post Modal */}
            <CelebrationPostModal
                isOpen={showModal}
                onClose={handleModalClose}
                employee={selectedEmployee}
                celebrationType={celebrationType}
                theme={theme}
                onPostCreated={onPostCreated}
            />
        </div>
    );
};

export default CelebrationHub;
